import { faComment } from '@fortawesome/free-regular-svg-icons';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useEffect, useMemo, useState } from 'react';
import useCommentCard from '~/hooks/discussion/useCommentCard';
import { useUserStatus } from '~/hooks/userStatus';
import {
  DISCUSSION_STATUS_REJECTED,
  DISCUSSION_STATUS_UNMODERATED,
  DiscussionStatus,
  IQuestionThreadProps,
  QuestionV2,
} from '~/lib/discussions/types';
import { mapToQuestionComment } from '~/lib/discussions/utils';
import { PDPContext } from '~/lib/product/context';
import useUser from '~/lib/user';
import { CommentType, CommentV2Type } from '~/static/discussions/enums';
import { UserObjectProps } from '~/types/user';
import { HelpfulButton } from '../HelpfulButton';
import { SubmitReply } from '../SubmitReply';
import { CreatorReply } from './CreatorReply';
import DiscussionActions from './DiscussionActions';
import DiscussionOptions from './DiscussionOptions';
import DiscussionQuestionInfo from './DiscussionQuestionInfo';
import DiscussionTag from './DiscussionTag';
import QuestionThread from './QuestionThread';

function QuestionCard({
  question,
  showCollapseIcon = false,
  expandCard = false,
  onDelete = () => {},
}: Readonly<{
  question: QuestionV2;
  showCollapseIcon?: boolean;
  expandCard?: boolean;
  onDelete?: (id: number) => void;
}>): JSX.Element {
  const [toggleThread, setToggleThread] = useState(false);

  const { user } = useUser();
  const { userStatus } = useUserStatus(question.deal_id);
  const {
    deal: { partner_user_ids = [] },
  } = useContext(PDPContext);

  const {
    comment,
    isPartner,
    stateHelpfulCount,
    stateHasUserUpvoted,
    toggleVote,
    disableVoteBtn,
    creatorReply,
    showDiscussionTag,
    isCollapsed,
    setCollapseValue,
    showStatusModerator,
    showSubmitReply,
    prependChild,
    setStatus,
    removePartnerStaffReply,
  } = useCommentCard(
    question,
    CommentV2Type.Question,
    partner_user_ids,
    expandCard,
    showCollapseIcon,
  );

  const questionThread: IQuestionThreadProps = useMemo(() => {
    const nonCreatorReplies = (comment?.children ?? []).filter(
      (child: QuestionV2) => child.id !== creatorReply?.id,
    );

    return {
      questionId: comment.id,
      comments: nonCreatorReplies.map((child) =>
        mapToQuestionComment(child, user as UserObjectProps, partner_user_ids),
      ),
    } as IQuestionThreadProps;
  }, [comment?.children, creatorReply?.id, partner_user_ids, comment.id, user]);

  const showThread = useMemo(() => {
    const isNotRejected =
      comment?.status?.toLowerCase() !== DISCUSSION_STATUS_REJECTED;
    const isRejectedWithComments =
      comment?.status?.toLowerCase() === DISCUSSION_STATUS_REJECTED &&
      (questionThread.comments ?? []).length > 0;

    return toggleThread && (isNotRejected || isRejectedWithComments);
  }, [comment?.status, questionThread.comments, toggleThread]);

  // enable threads for moderators when expanding the card
  useEffect(() => {
    if (!isCollapsed && showCollapseIcon) {
      setToggleThread(true);
    }
  }, [isCollapsed, showCollapseIcon]);

  return (
    <div
      data-testid="questions-card-wrapper"
      className="relative flex flex-col items-stretch gap-4 self-stretch rounded border border-gravel p-4 shadow-pdp"
    >
      <div className="absolute top-4 right-4 flex gap-3">
        {/* Only owner can see options */}
        {user?.id === comment.user.id && (
          <DiscussionOptions
            deleteUrl={`/api/v2/deals/${comment.deal_id}/questions/${comment.id}/`}
            discusstionType={CommentV2Type.Question}
            deletedCallback={() => onDelete(question.id)}
          />
        )}

        {showCollapseIcon && (
          <button
            title="Collapse review"
            onClick={setCollapseValue}
            className="px-0.5"
            data-testid="question-collapse-icon"
          >
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`${!isCollapsed && 'rotate-180'}`}
            />
          </button>
        )}
      </div>

      {showDiscussionTag && (
        <DiscussionTag
          status={comment.status?.toLowerCase() as DiscussionStatus}
          isOwner={user?.id === comment.user.id}
        />
      )}

      <DiscussionQuestionInfo
        comment={comment.comment}
        commentClass={user?.id === comment.user.id || (!showDiscussionTag && showCollapseIcon) ? 'pr-14' : ''}
        userName={comment.user.username}
        publishDate={new Date(comment.created)}
        detailUrl={comment.display_path}
      />

      {/* Collapsible content */}
      {(!isCollapsed || !showCollapseIcon) && (
        <>
          {!!creatorReply && (
            <CreatorReply
              {...creatorReply}
              className="first-letter:font-bold"
              onDelete={removePartnerStaffReply}
            />
          )}

          <div className="flex w-full items-center justify-between">
            <button
              data-testid="question-comments-button"
              className="text-sm font-medium text-bolt hover:text-bolt-light-20"
              onClick={() => setToggleThread(!toggleThread)}
              type="button"
            >
              <FontAwesomeIcon icon={faComment} className="mr-1" />
              Comment
              {!!questionThread.comments?.length && (
                <span>&nbsp;({questionThread.comments?.length})</span>
              )}
            </button>

            <HelpfulButton
              helpfulCount={stateHelpfulCount}
              hasUserUpvoted={stateHasUserUpvoted}
              isDisabled={disableVoteBtn}
              onToggleVote={toggleVote}
            />
          </div>
        </>
      )}

      {/* Partners are able to only Approve */}
      {showStatusModerator && (
        <DiscussionActions
          readOnly={
            !userStatus?.staff_moderator &&
            (!isPartner ||
              (isPartner &&
                comment.status?.toLowerCase() !==
                  DISCUSSION_STATUS_UNMODERATED))
          }
          canUndo={!!userStatus?.staff_moderator}
          canDecline={!!userStatus?.staff_moderator}
          status={comment.status?.toLowerCase() as DiscussionStatus}
          comment={comment}
          onChangeStatus={setStatus}
        />
      )}

      {(!isCollapsed || !showCollapseIcon) && (
        <>
          {showSubmitReply && (
            <SubmitReply
              dealId={comment.deal_id}
              comment={comment}
              type={CommentType.Question}
              onSuccess={prependChild}
            />
          )}

          {showThread && (
            <div className="w-full border-t border-sundog">
              <QuestionThread
                {...questionThread}
                onReply={prependChild}
                disableReply={
                  comment.status?.toLowerCase() === DISCUSSION_STATUS_REJECTED
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default QuestionCard;
